import { Drawer, Dropdown } from 'antd';
import { useForm } from 'antd/es/form/Form';
import { FormInstance } from 'antd/es/form/hooks/useForm';
import { get, set } from 'lodash-es';
import { MouseEvent as ReactMouseEvent, useEffect, useMemo, useState } from 'react';

import { sequenceFormValidation } from '@page/new-sequences/sequence-actions/sequence.form-validation';
import { Dots } from '@page/new-sequences/sequence-connection/sequence-connection.style';
import { Select } from '@style/global-style.app';

import { Button } from '@atom/button/button';
import { ButtonTypeEnum } from '@atom/button/button.enum';
import { Input } from '@atom/form/input/input';
import { Table } from '@atom/table/table';
import { Tabs } from '@atom/tabs/tabs';
import { Text, Title } from '@atom/typography/typography';
import { ColorEnum } from '@enum/style/color.enum';
import { Form } from '@form/common.style';
import { Label } from '@form/connector-auth/connector-auth.form-style';
import { isExist, isString } from '@util/data/is-exist';

import ActionIcon from '../assets/action.svg?react';
import AddIcon from '../assets/add.svg?react';
import { SequenceActionInterface } from './sequence-actions.interface';
import { actionMethodeOptions, ActionTabsEnum, bodyTypeOptions, rawBodyType } from './sequence-actions.options';
import { AddActionBlock, SequenceCard, SequenceCardWrapper, SequenceTitleWrapper } from './sequence-actions.style';

interface SequenceActionsProps {
  form: FormInstance;
}

export const SequenceActions = ({ form: mainForm }: SequenceActionsProps) => {
  const [activeIndex, setActiveIndex] = useState<number>(null!);
  const actions = Form.useWatch<SequenceActionInterface[]>('actions', mainForm) ?? [];
  const [currentTab, setCurrentTab] = useState<ActionTabsEnum>(ActionTabsEnum.Headers);
  const [options, setOptions] = useState<any[]>([{}]);
  const [form] = useForm();
  const type = Form.useWatch<string>('type', form);

  const shouldShowRaw = rawBodyType === type && currentTab === ActionTabsEnum.Body;
  const shouldShowRows = !shouldShowRaw || currentTab !== ActionTabsEnum.Body;

  const handleAddNewAction = (index: number) => () => {
    actions.splice(index, 0, {} as any);
    mainForm.setFieldsValue({ actions });
  };

  const handleRemoveAction = (index: number) => (event: ReactMouseEvent<HTMLElement, MouseEvent>) => {
    event.stopPropagation();
    actions.splice(index, 1);
    mainForm.setFieldsValue({ actions });
  };

  const handleAddAction = () =>
    form.validateFields().then((result) => {
      const currentActions = [...actions];
      set(currentActions, activeIndex, result);
      mainForm.setFieldsValue({ actions: currentActions });
      setActiveIndex(null!);
    });

  const handleAddParam = () => setOptions([...(options ?? []), {}]);

  useEffect(() => {
    const currentOptions = form.getFieldValue(currentTab) ?? [];
    setOptions(currentOptions);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTab, form]);

  useEffect(() => {
    if (isExist(activeIndex)) {
      const data = mainForm.getFieldValue('actions').at(activeIndex);
      form.setFieldsValue(data);
      setOptions(get(data, ActionTabsEnum.Headers, []));
    } else {
      form.resetFields();
      setOptions([]);
    }
    setCurrentTab(ActionTabsEnum.Headers);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeIndex, mainForm, form]);

  const columns = useMemo(
    () => [
      {
        title: 'Key',
        key: 'key',
        render: (_a: any, _b: any, index: number) => (
          <Label name={[currentTab, index, 'key']}>
            <Input placeholder='Please input key' size='large' />
          </Label>
        ),
      },
      {
        title: 'Value',
        key: 'value',
        render: (_a: any, _b: any, index: number) => (
          <Label name={[currentTab, index, 'value']}>
            <Input placeholder='Please input value' size='large' />
          </Label>
        ),
      },
    ],
    [currentTab],
  );

  return (
    <>
      <Label name='actions' style={{ display: 'none' }} />
      {actions.map((action, indexAction: number) => {
        const hasAction = isString(action.url);

        return (
          <>
            <AddActionBlock>
              <Button type={ButtonTypeEnum.Link} onClick={handleAddNewAction(indexAction)}>
                <AddIcon />
              </Button>
            </AddActionBlock>
            <SequenceCard>
              <SequenceCardWrapper onClick={() => setActiveIndex(indexAction)}>
                <ActionIcon />
                <SequenceTitleWrapper>
                  <Title color={ColorEnum.Black100} level={6}>
                    {indexAction + 2}. {hasAction ? action.method : 'Action'}
                  </Title>
                  <Text color={ColorEnum.Black70} level='s'>
                    {hasAction ? action.url : 'Add a sequence event that performs after start'}
                  </Text>
                </SequenceTitleWrapper>
                <Dropdown
                  placement='bottomRight'
                  menu={{
                    items: [
                      {
                        key: 'clear',
                        label: (
                          <Button size='small' type={ButtonTypeEnum.Error} onClick={handleRemoveAction(indexAction)}>
                            Remove
                          </Button>
                        ),
                      },
                    ],
                  }}
                >
                  <Dots />
                </Dropdown>
              </SequenceCardWrapper>
            </SequenceCard>
          </>
        );
      })}
      <Drawer
        open={isExist(activeIndex)}
        placement='right'
        size='large'
        title='Add Action'
        onClose={() => setActiveIndex(null!)}
      >
        <Form form={form} layout='vertical' name='action'>
          <Label label='Method' name='method' rules={[sequenceFormValidation]}>
            <Select options={actionMethodeOptions} placeholder='Select endpoint methode' size='large' showSearch />
          </Label>
          <Label label='Url' name='url' rules={[sequenceFormValidation]}>
            <Input placeholder='Please input url' size='large' />
          </Label>
          <Label label='Content Type' name='type'>
            <Select options={bodyTypeOptions} placeholder='Select content type' size='large' showSearch />
          </Label>
          <Tabs
            activeKey={currentTab}
            items={[
              {
                key: ActionTabsEnum.Headers,
                label: (
                  <>
                    Headers<Label name={ActionTabsEnum.Headers} style={{ display: 'none' }}></Label>
                  </>
                ),
              },
              {
                key: ActionTabsEnum.Params,
                label: (
                  <>
                    Params<Label name={ActionTabsEnum.Params} style={{ display: 'none' }}></Label>
                  </>
                ),
              },
              {
                key: ActionTabsEnum.Body,
                label: (
                  <>
                    Body<Label name={ActionTabsEnum.Body} style={{ display: 'none' }}></Label>
                  </>
                ),
              },
            ]}
            onChange={(value) => setCurrentTab(value as ActionTabsEnum)}
          />
          {shouldShowRows && (
            <Table
              bordered={false}
              columns={columns}
              dataSource={options}
              pagination={false}
              footer={() => (
                <Button size='small' type={ButtonTypeEnum.Default} onClick={handleAddParam}>
                  Add key
                </Button>
              )}
            />
          )}
          {shouldShowRaw && (
            <Label label='Raw data' name='body'>
              <Input.TextArea placeholder='Please input raw data' rows={8} size='large' />
            </Label>
          )}
          <Button type={ButtonTypeEnum.Primary} onClick={handleAddAction}>
            Save action
          </Button>
        </Form>
      </Drawer>
      <AddActionBlock isLast>
        <Button type={ButtonTypeEnum.Link} onClick={handleAddNewAction(actions.length)}>
          <AddIcon />
        </Button>
      </AddActionBlock>
    </>
  );
};
